import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, Users, Lock, DollarSign, BadgeCheck, AlertCircle, Crown, ChevronRight, Tag, Radio } from "lucide-react";
import MyListingsPanel from "@/components/MyListingsPanel";
import MyLivePanel from "@/components/MyLivePanel";
import { PushNotificationsToggle } from "@/components/InstallPWAPrompt";

const TABS = [
  { key: "privacy", label: "Privacy", icon: Shield },
  { key: "security", label: "Security · 2FA", icon: Lock },
  { key: "connections", label: "Friends", icon: Users },
  { key: "monetization", label: "Monetization", icon: DollarSign },
  { key: "listings", label: "My Listings", icon: Tag },
  { key: "live", label: "Live Now", icon: Radio },
  { key: "premium", label: "Premium", icon: Crown },
  { key: "blocked", label: "Blocked", icon: AlertCircle },
];

export default function Settings() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "privacy");

  useEffect(() => {
    document.title = "Settings — PFC";
    if (!loading && !user) nav("/login?next=/settings");
  }, [loading, user, nav]);

  if (loading || !user) return <div className="min-h-screen bg-ink-900 grid place-items-center text-white/40">Loading…</div>;

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="settings-page">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-10">
        <div className="text-pfc-gold text-[11px] tracking-[0.4em] uppercase font-sub mb-2">Account</div>
        <h1 className="font-display text-4xl">Settings</h1>
        <p className="text-white/60 text-sm mt-2">{user.email}</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-1" data-testid="settings-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 border-l-2 transition text-left ${
                  tab === t.key
                    ? "bg-white/5 border-pfc-gold text-pfc-gold"
                    : "border-transparent text-white/60 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span className="inline-flex items-center gap-3 text-sm">
                  <t.icon size={16} /> {t.label}
                </span>
                <ChevronRight size={14} className="opacity-50" />
              </button>
            ))}
          </aside>

          {/* Content */}
          <div className="border border-white/10 bg-ink-800/30 p-6 lg:p-8">
            {tab === "privacy" && <PrivacyTab />}
            {tab === "security" && <SecurityTab />}
            {tab === "connections" && <ConnectionsTab />}
            {tab === "monetization" && <MonetizationTab />}
            {tab === "listings" && <MyListingsPanel />}
            {tab === "live" && <MyLivePanel />}
            {tab === "premium" && <PremiumTab />}
            {tab === "blocked" && <BlockedTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== PRIVACY TAB ==============
function PrivacyTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/account/privacy").then((r) => setData(r.data));
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/account/privacy", patch);
      setData(d);
      toast.success("Privacy updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="text-white/40">Loading…</div>;

  return (
    <div data-testid="privacy-panel">
      <h2 className="font-display text-2xl">Privacy</h2>
      <p className="text-white/60 text-sm mt-1">Control who can see and contact you.</p>

      <Field label="Profile Visibility" desc="Who can see your profile">
        <RadioGroup
          name="visibility"
          value={data.visibility}
          options={[
            { v: "public", l: "Public — Anyone can view" },
            { v: "friends", l: "Friends only — Only accepted connections" },
            { v: "private", l: "Private — Only you" },
          ]}
          onChange={(v) => update({ visibility: v })}
          disabled={saving}
          testIdPrefix="vis"
        />
      </Field>

      <Field label="Messages" desc="Who can send you direct messages">
        <RadioGroup
          name="dm"
          value={data.dm_permissions}
          options={[
            { v: "everyone", l: "Everyone" },
            { v: "friends", l: "Friends only" },
            { v: "nobody", l: "Nobody" },
          ]}
          onChange={(v) => update({ dm_permissions: v })}
          disabled={saving}
          testIdPrefix="dm"
        />
      </Field>

      <Field label="Friend Requests" desc="Who can send you connection requests">
        <RadioGroup
          name="fr"
          value={data.friend_request_permissions}
          options={[
            { v: "everyone", l: "Everyone" },
            { v: "nobody", l: "Nobody" },
          ]}
          onChange={(v) => update({ friend_request_permissions: v })}
          disabled={saving}
          testIdPrefix="fr"
        />
      </Field>

      <Field label="Online Status">
        <Toggle
          checked={data.online_status_visible}
          onChange={(v) => update({ online_status_visible: v })}
          label="Show when I'm online"
          testId="online-toggle"
        />
      </Field>
    </div>
  );
}

// ============== SECURITY / 2FA TAB ==============
function SecurityTab() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/account/2fa/status").then((r) => setStatus(r.data));
  }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/account/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const enable = async () => {
    setLoading(true);
    try {
      await api.post("/account/2fa/enable", { code });
      toast.success("2FA enabled");
      setStatus({ enabled: true });
      setSetup(null);
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.post("/account/2fa/disable", { code });
      toast.success("2FA disabled");
      setStatus({ enabled: false });
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!status) return <div className="text-white/40">Loading…</div>;

  const qrUrl = setup ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(setup.otpauth_uri)}` : null;

  return (
    <div data-testid="security-panel">
      <h2 className="font-display text-2xl">Two-Factor Authentication</h2>
      <p className="text-white/60 text-sm mt-1">Add an extra layer of security to your account using an authenticator app (Google Authenticator, Authy, 1Password).</p>

      {status.enabled ? (
        <div className="mt-8">
          <div className="inline-flex items-center gap-2 text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 px-3 py-2 text-sm" data-testid="twofa-status-enabled">
            <BadgeCheck size={16} /> 2FA is currently enabled
          </div>
          <div className="mt-6 max-w-sm">
            <label className="block text-xs uppercase tracking-[0.3em] font-sub text-white/60 mb-2">Enter current 6-digit code to disable</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              data-testid="twofa-disable-code"
              className="w-full bg-black/40 border border-white/15 px-3 py-3 text-lg tracking-[0.5em] text-center text-white focus:border-pfc-gold focus:outline-none"
            />
            <button
              onClick={disable}
              disabled={loading || code.length !== 6}
              data-testid="twofa-disable-btn"
              className="mt-3 w-full py-3 bg-pfc-red/80 text-white font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-red transition disabled:opacity-50"
            >
              Disable 2FA
            </button>
          </div>
        </div>
      ) : !setup ? (
        <div className="mt-8">
          <div className="inline-flex items-center gap-2 text-white/60 bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="twofa-status-disabled">
            <Lock size={16} /> 2FA is not enabled
          </div>
          <button
            onClick={startSetup}
            disabled={loading}
            data-testid="twofa-start-btn"
            className="mt-6 px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-50"
          >
            Set Up 2FA
          </button>
        </div>
      ) : (
        <div className="mt-8 max-w-md">
          <div className="text-white/70 text-sm mb-4">1. Scan this QR code in your authenticator app:</div>
          <div className="bg-white p-3 inline-block">
            <img src={qrUrl} alt="QR Code" data-testid="twofa-qr" />
          </div>
          <details className="mt-3 text-xs text-white/60">
            <summary className="cursor-pointer hover:text-white">Or paste this secret manually</summary>
            <code className="block bg-black/40 p-2 mt-2 text-pfc-gold font-mono break-all" data-testid="twofa-secret">{setup.secret}</code>
          </details>
          <div className="mt-6">
            <label className="block text-xs uppercase tracking-[0.3em] font-sub text-white/60 mb-2">2. Enter the 6-digit code from your app</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              data-testid="twofa-verify-code"
              className="w-full bg-black/40 border border-white/15 px-3 py-3 text-lg tracking-[0.5em] text-center text-white focus:border-pfc-gold focus:outline-none"
            />
            <button
              onClick={enable}
              disabled={loading || code.length !== 6}
              data-testid="twofa-enable-btn"
              className="mt-3 w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-50"
            >
              Verify & Enable
            </button>
          </div>
        </div>
      )}

      {/* Push Notifications */}
      <div className="mt-10 pt-8 border-t border-white/10">
        <h3 className="font-display text-xl mb-3">Notifications</h3>
        <PushNotificationsToggle />
      </div>
    </div>
  );
}

// ============== CONNECTIONS TAB ==============
function ConnectionsTab() {
  const [data, setData] = useState({ accepted: [], incoming: [], outgoing: [] });

  const load = async () => {
    try {
      const { data } = await api.get("/connections/mine");
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/connections/${id}/${action}`);
      toast.success(action === "accept" ? "Accepted" : "Declined");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="connections-panel">
      <h2 className="font-display text-2xl">Friends · Connections</h2>
      <p className="text-white/60 text-sm mt-1">Manage your network — accept requests, view friends, block & report.</p>

      <Section title={`Incoming Requests (${data.incoming?.length || 0})`} testId="incoming-section">
        {data.incoming?.length === 0 && <Empty>No pending requests.</Empty>}
        {data.incoming?.map((c) => (
          <Row key={c.connection_id} user={c.other_user} testId={`incoming-${c.connection_id}`}>
            <button onClick={() => act(c.connection_id, "accept")} className="px-3 py-1.5 bg-pfc-gold text-ink-900 text-xs uppercase tracking-[0.2em] font-sub" data-testid={`accept-${c.connection_id}`}>Accept</button>
            <button onClick={() => act(c.connection_id, "reject")} className="px-3 py-1.5 border border-white/20 text-xs uppercase tracking-[0.2em] font-sub" data-testid={`reject-${c.connection_id}`}>Decline</button>
          </Row>
        ))}
      </Section>

      <Section title={`Friends (${data.accepted?.length || 0})`} testId="friends-section">
        {data.accepted?.length === 0 && <Empty>No friends yet.</Empty>}
        {data.accepted?.map((c) => (
          <Row key={c.connection_id} user={c.other_user} testId={`friend-${c.connection_id}`}>
            <BlockReportBtns targetId={c.other_user?.user_id} onAfter={load} />
          </Row>
        ))}
      </Section>

      <Section title={`Outgoing Requests (${data.outgoing?.length || 0})`} testId="outgoing-section">
        {data.outgoing?.length === 0 && <Empty>None pending.</Empty>}
        {data.outgoing?.map((c) => (
          <Row key={c.connection_id} user={c.other_user} testId={`outgoing-${c.connection_id}`}>
            <span className="text-xs text-white/40 uppercase tracking-[0.2em] font-sub">Pending</span>
          </Row>
        ))}
      </Section>
    </div>
  );
}

function BlockReportBtns({ targetId, onAfter }) {
  const block = async () => {
    if (!window.confirm("Block this user? You won't see each other's content.")) return;
    try {
      await api.post("/account/block", { target_user_id: targetId });
      toast.success("Blocked");
      onAfter?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const report = async () => {
    const reason = window.prompt("Report reason (spam, harassment, impersonation, other):");
    if (!reason) return;
    try {
      await api.post("/account/report", { target_user_id: targetId, reason });
      toast.success("Report submitted to moderation");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  return (
    <>
      <button onClick={block} className="px-3 py-1.5 border border-pfc-red/40 text-pfc-red text-xs uppercase tracking-[0.2em] font-sub" data-testid={`block-${targetId}`}>Block</button>
      <button onClick={report} className="px-3 py-1.5 border border-white/20 text-white/70 text-xs uppercase tracking-[0.2em] font-sub" data-testid={`report-${targetId}`}>Report</button>
    </>
  );
}

// ============== MONETIZATION TAB ==============
function MonetizationTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCashout, setShowCashout] = useState(false);

  const load = async () => {
    const { data } = await api.get("/creator/me");
    setData(data);
    setForm({
      monetization_enabled: data.monetization_enabled,
      subscription_price: data.subscription_price || 0,
      tips_enabled: data.tips_enabled,
      ppv_enabled: data.ppv_enabled,
      bio: data.bio || "",
    });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-white/40">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, subscription_price: parseFloat(form.subscription_price) || 0 };
      const { data: d } = await api.put("/creator/settings", payload);
      setData(d);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cashoutBalance = data.tips_received_credits;
  const cashoutUsd = (cashoutBalance / data.tip_credits_per_dollar).toFixed(2);

  return (
    <div data-testid="monetization-panel">
      <h2 className="font-display text-2xl">Creator Monetization</h2>
      <p className="text-white/60 text-sm mt-1">Enable tips, subscriptions, and pay-per-view posts. Tips are received in casino credits — convert to USD via cashout ({data.tip_credits_per_dollar} credits = $1).</p>

      <Field label="Monetization">
        <Toggle
          checked={!!form.monetization_enabled}
          onChange={(v) => setForm((s) => ({ ...s, monetization_enabled: v }))}
          label="I want to monetize my profile"
          testId="mon-toggle"
        />
      </Field>

      <Field label="Accept Tips" desc="Fans can send you casino credits as tips">
        <Toggle
          checked={!!form.tips_enabled}
          onChange={(v) => setForm((s) => ({ ...s, tips_enabled: v }))}
          disabled={!form.monetization_enabled}
          label="Tips enabled"
          testId="tips-toggle"
        />
      </Field>

      <Field label="Subscribers" desc="Monthly USD price (set to 0 to disable)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.subscription_price}
          onChange={(e) => setForm((s) => ({ ...s, subscription_price: e.target.value }))}
          disabled={!form.monetization_enabled}
          data-testid="sub-price-input"
          className="w-40 bg-black/40 border border-white/15 px-3 py-2 text-white focus:border-pfc-gold focus:outline-none disabled:opacity-50"
          placeholder="0.00"
        />
        <span className="ml-2 text-white/50 text-sm">USD / month</span>
      </Field>

      <Field label="Pay-Per-View Posts" desc="Lock individual posts behind a one-time price (set per post in Feed)">
        <Toggle
          checked={!!form.ppv_enabled}
          onChange={(v) => setForm((s) => ({ ...s, ppv_enabled: v }))}
          disabled={!form.monetization_enabled}
          label="PPV enabled"
          testId="ppv-toggle"
        />
      </Field>

      <Field label="Creator Bio" desc="Short pitch shown on your tip/subscribe widget">
        <textarea
          value={form.bio}
          onChange={(e) => setForm((s) => ({ ...s, bio: e.target.value }))}
          rows={3}
          maxLength={500}
          data-testid="creator-bio"
          className="w-full bg-black/40 border border-white/15 px-3 py-2 text-white focus:border-pfc-gold focus:outline-none"
          placeholder="Tell your supporters what they're funding…"
        />
      </Field>

      <button onClick={save} disabled={saving} data-testid="save-monetization-btn" className="px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright disabled:opacity-50">
        {saving ? "Saving…" : "Save Monetization Settings"}
      </button>

      <PaymentHandlesBlock />

      {/* Earnings Summary */}
      <div className="mt-10 border-t border-white/10 pt-8">
        <h3 className="font-display text-xl">Earnings</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
          <Stat label="Tip Balance" value={`${cashoutBalance}`} sub={`$${cashoutUsd}`} testId="stat-balance" />
          <Stat label="Lifetime Tips" value={`${data.tips_lifetime_credits}`} sub={`credits`} testId="stat-lifetime-tips" />
          <Stat label="Subscribers" value={`${data.subscriber_count}`} sub={`active`} testId="stat-subs" />
          <Stat label="Sub Revenue" value={`$${data.subscription_lifetime_usd.toFixed(2)}`} sub={`lifetime`} testId="stat-sub-rev" />
        </div>
        <button onClick={() => setShowCashout(true)} disabled={cashoutBalance < 100} data-testid="request-cashout-btn" className="mt-5 px-5 py-2.5 border border-pfc-gold text-pfc-gold font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold hover:text-ink-900 transition disabled:opacity-40">
          Request Cashout
        </button>
        {cashoutBalance < 100 && <p className="mt-2 text-xs text-white/40">Minimum 100 credits ($1) to cash out.</p>}
      </div>

      {showCashout && (
        <CashoutModal
          balance={cashoutBalance}
          rate={data.tip_credits_per_dollar}
          onClose={() => setShowCashout(false)}
          onDone={() => { setShowCashout(false); load(); }}
        />
      )}
    </div>
  );
}

function CashoutModal({ balance, rate, onClose, onDone }) {
  const [credits, setCredits] = useState(balance);
  const [method, setMethod] = useState("paypal");
  const [handle, setHandle] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (credits < 100 || credits > balance) { toast.error(`Enter between 100 and ${balance}.`); return; }
    if (!handle.trim()) { toast.error("Payout handle is required."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/creator/cashout", { credits: parseInt(credits, 10), payout_method: method, payout_handle: handle.trim() });
      toast.success(`Cashout requested: $${data.amount_usd.toFixed(2)}`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const amountUsd = (Number(credits) / rate).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="cashout-modal">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-md w-full p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="font-display text-2xl text-white">Request Cashout</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm" data-testid="close-cashout">Close</button>
        </div>
        <div className="bg-white/5 px-4 py-3 mb-5 text-sm">
          <div className="text-white/60 text-xs uppercase tracking-[0.3em] font-sub">Available Balance</div>
          <div className="text-pfc-gold font-display text-xl mt-1">{balance} credits · ${(balance / rate).toFixed(2)}</div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Credits to Cash Out</div>
            <input
              type="number"
              min="100"
              max={balance}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              data-testid="cashout-credits"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none"
            />
            <div className="text-xs text-white/50 mt-1">= ${amountUsd} USD · Min 100 credits</div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Payout Method</div>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="cashout-method"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none"
            >
              <option value="paypal">PayPal</option>
              <option value="venmo">Venmo</option>
              <option value="cashapp">Cash App</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Payout Handle / Email</div>
            <input
              required
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder={method === "paypal" ? "you@email.com" : method === "bank" ? "Account reference" : "@your-handle"}
              data-testid="cashout-handle"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="confirm-cashout-btn"
            className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-50"
          >
            {loading ? "Submitting…" : `Request $${amountUsd} Cashout`}
          </button>
          <p className="text-[11px] text-white/40 text-center">PFC processes cashouts within 5 business days via email.</p>
        </form>
      </div>
    </div>
  );
}

// ============== PREMIUM TAB ==============
function PremiumTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/creator/me").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-white/40">Loading…</div>;

  const subscribe = async (plan) => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/creator/premium/checkout", { plan, origin_url: window.location.origin });
      if (res.url) window.location.href = res.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div data-testid="premium-panel">
      <h2 className="font-display text-2xl">PFC Premium Creator</h2>
      <p className="text-white/60 text-sm mt-1">Unlock premium tools: priority badge, advanced analytics, higher post limits, subscribers-only posts, priority support.</p>

      {data.premium_active && (
        <div className="mt-6 inline-flex items-center gap-2 text-pfc-gold bg-pfc-gold/10 border border-pfc-gold/40 px-3 py-2 text-sm" data-testid="premium-active-badge">
          <Crown size={16} /> Premium active until {new Date(data.premium_until).toLocaleDateString()}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        <PlanCard
          name="Monthly"
          price="$9.99"
          per="/month"
          features={["Priority badge", "Subscriber-only posts", "Advanced analytics", "Cancel anytime"]}
          onClick={() => subscribe("creator_premium_monthly")}
          loading={loading}
          testId="plan-monthly"
        />
        <PlanCard
          name="Yearly"
          price="$199"
          per="/year"
          highlight="SAVE $20"
          features={["Everything in Monthly", "2 months free vs monthly", "Locked-in rate", "Early access to new tools"]}
          onClick={() => subscribe("creator_premium_yearly")}
          loading={loading}
          testId="plan-yearly"
        />
      </div>
    </div>
  );
}

function PlanCard({ name, price, per, highlight, features, onClick, loading, testId }) {
  return (
    <div className={`relative border p-6 ${highlight ? "border-pfc-gold bg-pfc-gold/5" : "border-white/15 bg-ink-800/30"}`} data-testid={testId}>
      {highlight && <div className="absolute -top-2.5 right-4 bg-pfc-gold text-ink-900 text-[10px] font-sub tracking-[0.25em] px-2 py-0.5 uppercase">{highlight}</div>}
      <div className="text-xs uppercase tracking-[0.3em] font-sub text-pfc-gold">{name}</div>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="font-display text-4xl">{price}</span>
        <span className="text-white/60 text-sm">{per}</span>
      </div>
      <ul className="mt-5 space-y-2 text-sm text-white/70">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2"><BadgeCheck size={14} className="text-pfc-gold mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      <button onClick={onClick} disabled={loading} className="mt-6 w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-50">
        {loading ? "…" : `Start ${name}`}
      </button>
    </div>
  );
}

// ============== BLOCKED TAB ==============
function BlockedTab() {
  const [blocked, setBlocked] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/account/blocked");
      setBlocked(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const unblock = async (id) => {
    try {
      await api.post("/account/unblock", { target_user_id: id });
      toast.success("Unblocked");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="blocked-panel">
      <h2 className="font-display text-2xl">Blocked Users</h2>
      <p className="text-white/60 text-sm mt-1">People you&apos;ve blocked won&apos;t be able to find or message you.</p>
      <div className="mt-6 space-y-2">
        {blocked.length === 0 && <Empty>You haven&apos;t blocked anyone.</Empty>}
        {blocked.map((b) => (
          <div key={b.user_id} className="border border-white/10 p-3 flex items-center justify-between" data-testid={`blocked-${b.user_id}`}>
            <div className="flex items-center gap-3">
              {b.avatar ? <img src={b.avatar} alt="" className="h-9 w-9 object-cover" /> : <div className="h-9 w-9 bg-white/5" />}
              <div>
                <div className="text-sm text-white">{b.name}</div>
                <div className="text-[11px] text-white/40">{b.user_id}</div>
              </div>
            </div>
            <button onClick={() => unblock(b.user_id)} className="px-3 py-1.5 border border-white/20 text-white/80 text-xs uppercase tracking-[0.2em] font-sub" data-testid={`unblock-${b.user_id}`}>Unblock</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== Shared UI ===============
function Field({ label, desc, children }) {
  return (
    <div className="mt-6">
      <div className="text-xs uppercase tracking-[0.3em] font-sub text-white/60">{label}</div>
      {desc && <div className="text-xs text-white/40 mt-0.5">{desc}</div>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function RadioGroup({ name, value, options, onChange, disabled, testIdPrefix }) {
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <label key={o.v} className={`flex items-center gap-3 cursor-pointer ${disabled ? "opacity-50" : ""}`}>
          <input
            type="radio"
            name={name}
            value={o.v}
            checked={value === o.v}
            onChange={() => onChange(o.v)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-${o.v}`}
            className="accent-pfc-gold"
          />
          <span className="text-sm text-white/80">{o.l}</span>
        </label>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, label, disabled, testId }) {
  return (
    <label className={`inline-flex items-center gap-3 cursor-pointer ${disabled ? "opacity-50" : ""}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testId}
        className={`relative w-11 h-6 transition ${checked ? "bg-pfc-gold" : "bg-white/15"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
      <span className="text-sm text-white/80">{label}</span>
    </label>
  );
}

function Section({ title, children, testId }) {
  return (
    <div className="mt-7" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.3em] font-sub text-pfc-gold mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ user, children, testId }) {
  return (
    <div className="border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={testId}>
      <div className="flex items-center gap-3 min-w-0">
        {user?.avatar ? <img src={user.avatar} alt="" className="h-9 w-9 object-cover" /> : <div className="h-9 w-9 bg-white/5" />}
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{user?.name || "Member"}</div>
          {user?.headline && <div className="text-[11px] text-white/40 truncate">{user.headline}</div>}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-white/40 text-sm py-3">{children}</div>;
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="border border-white/10 bg-ink-800/30 p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/50">{label}</div>
      <div className="font-display text-2xl mt-1 text-pfc-gold">{value}</div>
      <div className="text-xs text-white/40 mt-1">{sub}</div>
    </div>
  );
}

function PaymentHandlesBlock() {
  const [cashapp, setCashapp] = useState("");
  const [paypal, setPaypal] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [urls, setUrls] = useState({});

  useEffect(() => {
    api.get("/account/payment-handles").then((r) => {
      setCashapp(r.data.cashapp_handle || "");
      setPaypal(r.data.paypal_handle || "");
      setUrls({ cashapp: r.data.cashapp_url, paypal: r.data.paypal_url });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/account/payment-handles", {
        cashapp_handle: cashapp || null,
        paypal_handle: paypal || null,
      });
      setUrls({ cashapp: data.cashapp_url, paypal: data.paypal_url });
      toast.success("Payment handles saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="mt-10 pt-8 border-t border-white/10" data-testid="payment-handles">
      <h3 className="font-display text-xl">Direct Payments · Tips &amp; Donations</h3>
      <p className="text-white/60 text-sm mt-1">Add your CashApp + PayPal handles so fans can tip you instantly. We show "Send via CashApp" and "Send via PayPal" buttons on your profile.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 max-w-2xl">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">CashApp $cashtag</div>
          <div className="flex items-stretch">
            <span className="px-3 grid place-items-center bg-white/5 border border-r-0 border-white/15 text-white/70">$</span>
            <input value={cashapp} onChange={(e) => setCashapp(e.target.value.replace(/[$ ]/g, ""))} placeholder="yourcashtag" data-testid="cashapp-input" className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
          </div>
          {urls.cashapp && <a href={urls.cashapp} target="_blank" rel="noreferrer" className="text-pfc-gold text-[11px] mt-1 inline-block hover:underline" data-testid="cashapp-preview">{urls.cashapp}</a>}
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">PayPal.me handle / email</div>
          <input value={paypal} onChange={(e) => setPaypal(e.target.value)} placeholder="yourhandle or you@email.com" data-testid="paypal-input" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
          {urls.paypal && <a href={urls.paypal} target="_blank" rel="noreferrer" className="text-pfc-gold text-[11px] mt-1 inline-block hover:underline" data-testid="paypal-preview">{urls.paypal}</a>}
        </label>
      </div>
      <button onClick={save} disabled={saving} data-testid="save-payment-handles-btn" className="mt-4 px-5 py-2.5 border border-pfc-gold text-pfc-gold font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold hover:text-ink-900 transition disabled:opacity-50">
        {saving ? "Saving…" : "Save Payment Handles"}
      </button>
    </div>
  );
}
